import React, { useState, useRef, useEffect } from 'react';
import config from '../config/environment';

const Matchmaking = ({ onNavigate }) => {
  const [playerName, setPlayerName] = useState('');
  const [boardSize, setBoardSize] = useState(15);
  const [winCondition, setWinCondition] = useState(5);
  const [timeControl, setTimeControl] = useState('unlimited');
  const [rating, setRating] = useState(1000);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');
  const [searchStartTime, setSearchStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [queueStats, setQueueStats] = useState(null);

  const wsRef = useRef(null);
  const intervalRef = useRef(null);

  const timeControlOptions = {
    unlimited: { time: 0, label: 'Unlimited' },
    blitz: { time: 300, label: 'Blitz (5 min)' },
    rapid: { time: 600, label: 'Rapid (10 min)' },
    long: { time: 1800, label: 'Long (30 min)' }
  };

  const boardSizeOptions = [
    { value: 15, label: '15×15 (Standard)' },
    { value: 19, label: '19×19 (Large)' },
    { value: 13, label: '13×13 (Medium)' },
    { value: 11, label: '11×11 (Small)' }
  ];

  const winConditionOptions = [
    { value: 5, label: '5 in a row (Standard)' },
    { value: 4, label: '4 in a row' },
    { value: 6, label: '6 in a row' }
  ];

  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isSearching && searchStartTime) {
      intervalRef.current = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - searchStartTime) / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isSearching, searchStartTime]);

  const connectToMatchmaking = () => {
    const wsUrl = `${config.getWebSocketUrl()}/ws/matchmaking`;
    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      // Request queue stats
      wsRef.current.send(JSON.stringify({
        type: 'getQueueStats'
      }));
    };

    wsRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      
      switch (message.type) {
        case 'searchStarted':
          setIsSearching(true);
          setSearchStartTime(Date.now());
          setElapsedTime(0);
          break;
          
        case 'matchFound':
          setIsSearching(false);
          onNavigate('game', {
            roomId: message.roomId,
            playerName: playerName,
            isMatchmaking: true
          });
          break;
          
        case 'searchCancelled':
          setIsSearching(false);
          setSearchStartTime(null);
          setElapsedTime(0);
          break;
          
        case 'queueStats':
          setQueueStats(message.stats);
          break;
          
        case 'error':
          setError(message.message);
          setIsSearching(false);
          break;
          
        default:
          break;
      }
    };

    wsRef.current.onclose = () => {
      setIsSearching(false);
    };

    wsRef.current.onerror = () => {
      setError('Failed to connect to matchmaking server');
      setIsSearching(false);
    };
  };

  const startSearch = () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }

    setError('');
    connectToMatchmaking();

    // Wait for connection to open
    const checkConnection = () => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'findMatch',
          playerName: playerName,
          boardSize: boardSize,
          winCondition: winCondition,
          timeControl: timeControl,
          rating: rating
        }));
      } else {
        setTimeout(checkConnection, 100);
      }
    };
    
    checkConnection();
  };

  const cancelSearch = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cancelSearch'
      }));
    }
    
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    setIsSearching(false);
    setSearchStartTime(null);
    setElapsedTime(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="container">
      <div className="card">
        <h2>Find Opponent</h2>
        
        {!isSearching ? (
          <div className="form-container">
            <div className="form-group">
              <label htmlFor="playerName">Your Name</label>
              <input
                type="text"
                id="playerName"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                maxLength={20}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="boardSize">Board Size</label>
                <select
                  id="boardSize"
                  value={boardSize}
                  onChange={(e) => setBoardSize(parseInt(e.target.value))}
                >
                  {boardSizeOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="winCondition">Win Condition</label>
                <select
                  id="winCondition"
                  value={winCondition}
                  onChange={(e) => setWinCondition(parseInt(e.target.value))}
                >
                  {winConditionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="timeControl">Time Control</label>
              <select
                id="timeControl"
                value={timeControl}
                onChange={(e) => setTimeControl(e.target.value)}
              >
                {Object.entries(timeControlOptions).map(([key, option]) => (
                  <option key={key} value={key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="rating">Rating: {rating}</label>
              <input
                type="range"
                id="rating"
                min="500"
                max="2000"
                step="50"
                value={rating}
                onChange={(e) => setRating(parseInt(e.target.value))}
              />
              <div className="rating-range">
                <span>500</span>
                <span>2000</span>
              </div>
            </div>

            {queueStats && (
              <div className="queue-stats">
                <h3>Queue Statistics</h3>
                <p>Players in queue: {queueStats.totalPlayers}</p>
                {queueStats.totalPlayers > 0 && (
                  <div className="stats-details">
                    <p>By board size:</p>
                    <ul>
                      {Object.entries(queueStats.byBoardSize).map(([size, count]) => (
                        <li key={size}>{size}×{size}: {count} players</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {error && <div className="error">{error}</div>}

            <div className="form-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => onNavigate('home')}
              >
                Back
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={startSearch}
                disabled={!playerName.trim()}
              >
                Find Match
              </button>
            </div>
          </div>
        ) : (
          <div className="searching-container">
            <div className="searching-animation">
              <div className="spinner"></div>
            </div>
            <h3>Searching for opponent...</h3>
            <div className="search-info">
              <p>Board: {boardSize}×{boardSize}</p>
              <p>Win condition: {winCondition} in a row</p>
              <p>Time control: {timeControlOptions[timeControl].label}</p>
              <p>Your rating: {rating}</p>
            </div>
            <div className="search-time">
              <p>Search time: {formatTime(elapsedTime)}</p>
            </div>
            <button
              type="button"
              className="danger-button"
              onClick={cancelSearch}
            >
              Cancel Search
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Matchmaking;
